import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import clsx from "clsx";
import { FileText, FileType, MessageSquare, StickyNote } from "lucide-react";
import { FC, useEffect, useMemo } from "react";
import { Tabs, TabsContent, TabsList } from "~/components/ui/tabs";
import { NotesArea } from "~/features/notes/components/NotesArea";
import { useSkyViewStore } from "~/features/skyView/store";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import { ChatDocumentTab, useTabOrder } from "~/hooks/useTabOrder";
import { trackEvent } from "~/utils/analytics";
import { ChatArea } from "../ChatArea";
import { ReportArea } from "../reportArea";
import { SummaryArea } from "../SummaryArea";
import { ExpandableControl } from "./ExpandableControl";
import { SortableTabTrigger } from "./SortableTabTrigger";

const TABS = [
  {
    id: "report",
    label: "chat.report.title",
    icon: FileText,
  },
  {
    id: "notes",
    label: "common.notes",
    icon: StickyNote,
  },
  {
    id: "chat",
    label: "common.chat",
    icon: MessageSquare,
  },
  {
    id: "summary",
    label: "common.summary",
    icon: FileType,
  },
];

export const DocSidebar: FC = () => {
  const [{ tab }, updateParam] = useDocumentSearchParams();
  const isExpanded = useSkyViewStore((state) => state.isExpanded);
  const { tabOrder, saveTabOrder } = useTabOrder();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const reorderedTabs = useMemo(() => {
    return tabOrder
      .map((tabId) => TABS.find((tab) => tab.id === tabId)!)
      .filter(Boolean);
  }, [tabOrder]);

  useEffect(() => {
    if (!tab) {
      updateParam({
        tab: "report",
      });
    }
  }, [tab, updateParam]);

  const handleTabChange = (value: string) => {
    const eventName = `app/doc_sidebar/${value}_tab.trigger/click`;
    trackEvent({ eventName });

    updateParam({
      tab: value as ChatDocumentTab,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = tabOrder.indexOf(active.id as ChatDocumentTab);
    const newIndex = tabOrder.indexOf(over.id as ChatDocumentTab);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = [...tabOrder];
      const [removed] = newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, removed);

      saveTabOrder(newOrder);

      trackEvent({
        eventName: "app/doc_sidebar/tab_reorder/drag",
        properties: {
          from: oldIndex,
          to: newIndex,
          tabId: active.id,
        },
      });
    }
  };

  return (
    <div
      className={clsx("flex h-full", {
        "col-span-3": isExpanded,
      })}
    >
      <ExpandableControl />
      {isExpanded && (
        <div className="border-zinc-200 w-full min-w-0">
          <Tabs value={tab} onValueChange={handleTabChange}>
            <div className="h-[60px] border-b border-zinc-200 flex items-center">
              <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={tabOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  <TabsList
                    className={`grid w-full grid-cols-${TABS.length} mx-3`}
                  >
                    {reorderedTabs.map((tab) => (
                      <SortableTabTrigger
                        key={tab.id}
                        id={tab.id}
                        value={tab.id}
                        label={tab.label}
                        icon={tab.icon}
                      />
                    ))}
                  </TabsList>
                </SortableContext>
              </DndContext>
            </div>
            {reorderedTabs.map((tabItem) => (
              <TabsContent key={tabItem.id} value={tabItem.id}>
                {tabItem.id === "report" && <ReportArea />}
                {tabItem.id === "notes" && (
                  <NotesArea isActiveTab={tab === "notes"} />
                )}
                {tabItem.id === "chat" && <ChatArea />}
                {tabItem.id === "summary" && <SummaryArea />}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      )}
    </div>
  );
};
